import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import confetti from 'canvas-confetti';
import PropTypes from 'prop-types';
import useAudio from '../../hooks/useAudio';
import useNumbersGame from '../../hooks/useNumbersGame';
import { Volume2, RotateCcw, Trophy, Star, Home, Shuffle} from 'lucide-react';
import { Link } from 'react-router-dom';
import useAnalytics from '../../hooks/useAnalytics';

// Scoring constants
const SCORING_CONSTANTS = {
  BASE_POINTS: 50,
  SPEED_BONUS_MAX: 100,
  PERFECT_MATCH_BONUS: 200,
  LEVEL_COMPLETE_BONUS: 300,
  MAX_SPEED_BONUS_TIME: 5, // seconds
  MIN_ACCURACY_FOR_STARS: 80
};

// Level configurations - expanded to 12 levels
const LEVEL_CONFIGS = {
  1: { 
    pairs: 3, 
    title: "Tiny Numbers",
    description: "Start with 0-2!",
    passingScore: 70,
    range: [0, 2]
  },
  2: { 
    pairs: 4, 
    title: "Small Numbers",
    description: "Master 0-3!",
    passingScore: 75,
    range: [0, 3]
  },
  3: { 
    pairs: 5, 
    title: "Growing Up",
    description: "Learn 0-4!",
    passingScore: 75,
    range: [0, 4]
  },
  4: { 
    pairs: 6, 
    title: "Big Numbers",
    description: "Try 0-5!",
    passingScore: 80,
    range: [0, 5]
  },
  5: { 
    pairs: 8, 
    title: "Number Hero",
    description: "Challenge 0-7!",
    passingScore: 80,
    range: [0, 7]
  },
  6: { 
    pairs: 10, 
    title: "Number Master",
    description: "Conquer 0-9!",
    passingScore: 85,
    range: [0, 9]
  },
  7: { 
    pairs: 8, 
    title: "Teen Numbers",
    description: "Tackle 10-17!",
    passingScore: 80,
    range: [10, 17]
  },
  8: { 
    pairs: 10, 
    title: "Twenty Club",
    description: "Master 10-19!",
    passingScore: 85,
    range: [10, 19]
  },
  9: { 
    pairs: 12, 
    title: "Fifty Challenge",
    description: "Explore 0-49!",
    passingScore: 85,
    range: [0, 49],
    isRandom: true
  },
  10: { 
    pairs: 15, 
    title: "Century Explorer",
    description: "Journey 0-99!",
    passingScore: 85,
    range: [0, 99],
    isRandom: true
  },
  11: { 
    pairs: 18, 
    title: "Big Numbers Pro",
    description: "Conquer 0-149!",
    passingScore: 90,
    range: [0, 149],
    isRandom: true
  },
  12: { 
    pairs: 20, 
    title: "Ultimate Master",
    description: "Rule 0-200!",
    passingScore: 90,
    range: [0, 200],
    isRandom: true
  }
};

const ArabicNumberCardMatchingGame = () => {
  // Initialize hooks - using your existing hooks!
  const { 
    userId,
    level,
    setLevel,
    maxLevel,
    numbersData,
    bestScores,
    isLoading,
    error,
    saveLevelData,
    updateLevel,
    unlockNextLevel,
    resetAllProgress
  } = useNumbersGame('ArabicNumberCardMatchingGame');

  const {
    playCorrectSound,
    playIncorrectSound,
    playPerfectSound,
    playStreakSound,
    playWhooshSound,
    playStarSound,
    startBackgroundMusic,
    AudioElements
  } = useAudio();

  const { saveSessionData } = useAnalytics();

  // Refs for audio management
  const audioRef = useRef(null);
  const audioTimeoutRef = useRef(null);
  
  // Timing State
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionMistakes, setSessionMistakes] = useState([]);

  // Game state
  const [gamePhase, setGamePhase] = useState('menu'); // 'menu', 'playing', 'complete'
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Scoring state
  const [totalScore, setTotalScore] = useState(0);
  const [correctMatches, setCorrectMatches] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [gameStartTime, setGameStartTime] = useState(Date.now());
  const [lastMatchTime, setLastMatchTime] = useState(Date.now());
  const [stars, setStars] = useState(0);
  
  // UI state
  const [celebrations, setCelebrations] = useState(false);
  const [sparkles, setSparkles] = useState(false);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [levelCompleteData, setLevelCompleteData] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  
  // Get current level config
  const levelConfig = useMemo(() => LEVEL_CONFIGS[level] || LEVEL_CONFIGS[1], [level]);
  
  // Utility functions
  const shuffleArray = useCallback((array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }, []);

  // Audio playback using your existing audio system
  const playNumberAudio = useCallback(async (number) => {
    console.log('🔊 Playing audio for number:', number);
    
    // Clear any existing audio and timeouts
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
      audioRef.current = null;
    }
    
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
      audioTimeoutRef.current = null;
    }

    const numberData = numbersData.find(n => n.englishNumeral === number);
    if (!numberData?.audioURL) {
      console.log(`No audio available for number ${number}`);
      return;
    }

    setAudioPlaying(true);

    try {
      const audio = new Audio();
      audioRef.current = audio;
      
      // Set up a safety timeout
      audioTimeoutRef.current = setTimeout(() => {
        console.log('🔊 Audio timeout - stopping playback');
        cleanupAudio();
      }, 5000);
      
      // Create cleanup function that removes listeners
      const cleanupAudio = () => {
        setAudioPlaying(false);
        if (audioTimeoutRef.current) {
          clearTimeout(audioTimeoutRef.current);
          audioTimeoutRef.current = null;
        }
        
        // Remove event listeners before cleanup
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
        
        // Clear the audio
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.src = '';
        } catch (e) {
          // Ignore cleanup errors
        }
        
        audioRef.current = null;
      };
      
      const handleEnded = () => {
        console.log('🔊 Audio ended normally');
        cleanupAudio();
      };
      
      const handleError = (error) => {
        console.warn('🔊 Audio error for number', number, ':', error);
        cleanupAudio();
      };
      
      // Add event listeners
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      
      audio.volume = 0.8;
      audio.src = numberData.audioURL;
      
      await audio.play();
      
    } catch (error) {
      console.error('🔊 Failed to play audio:', error);
      setAudioPlaying(false);
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
    }
  }, [numbersData]);

  const lastCelebrationRef = useRef(0);
  const triggerCelebration = useCallback((isMatch = false, earnedStars = 0) => {
    // Throttle celebrations to prevent spam
    const now = Date.now();
    if (now - lastCelebrationRef.current < 500) {
      return;
    }
    lastCelebrationRef.current = now;
    
    setCelebrations(true);
    setSparkles(true);
    
    let colors = ['#FF6B6B', '#4ECDC4', '#45B7D1'];
    let particleCount = 30;
    let shapes = ['circle'];
    
    if (earnedStars > 0) {
      colors = ['#FFD700', '#FFA500'];
      shapes = ['star'];
      particleCount = 50;
      playStarSound();
    } else if (isMatch) {
      colors = ['#00FF88', '#00DDFF'];
      particleCount = 40;
      playCorrectSound();
    }
    
    // Confetti celebration
    confetti({
      particleCount,
      spread: 50,
      origin: { y: 0.7 },
      colors,
      shapes,
      gravity: 1.2,
      drift: 0
    });

    setTimeout(() => {
      setCelebrations(false);
      setSparkles(false);
    }, 1500);
  }, [playStarSound, playCorrectSound]);

  const calculateSpeedBonus = useCallback((responseTime) => {
    const maxTime = SCORING_CONSTANTS.MAX_SPEED_BONUS_TIME * 1000;
    if (responseTime <= maxTime) {
      const bonus = Math.round(SCORING_CONSTANTS.SPEED_BONUS_MAX * (1 - responseTime / maxTime));
      return Math.max(0, bonus);
    }
    return 0;
  }, []);

  const calculateStars = useCallback((accuracy, attempts, correctMatches) => {
    console.log('🐛 calculateStars called with:', { accuracy, attempts, correctMatches });
    
    // Primary factor: accuracy
    if (accuracy >= 80) {
        console.log('🐛 Returning 3 stars (95%+)');
        return 3;
    }
    if (accuracy >= 70) {
        console.log('🐛 Returning 2 stars (85%+)');
        return 2;
    }
    if (accuracy >= 50) {
        console.log('🐛 Returning 1 star (70%+)');
        return 1;
    }
    console.log('🐛 Returning 0 stars (<70%)');
    return 0;
  }, []);

  const initializeLevel = useCallback(() => {
    if (!numbersData || numbersData.length === 0) {
      console.log('Numbers data not loaded yet');
      return;
    }

    const [min, max] = levelConfig.range;
    let levelNumbers = [];
    
    if (levelConfig.isRandom) {
      // For random levels, pick random numbers from the range
      const availableNumbers = numbersData
        .filter(n => n.englishNumeral >= min && n.englishNumeral <= max)
        .map(n => n.englishNumeral);
      
      // Randomly select the required number of pairs
      const shuffledAvailable = shuffleArray(availableNumbers);
      levelNumbers = shuffledAvailable.slice(0, levelConfig.pairs);
    } else {
      // For sequential levels, get numbers in order
      for (let i = min; i <= max && levelNumbers.length < levelConfig.pairs; i++) {
        const numberData = numbersData.find(n => n.englishNumeral === i);
        if (numberData) {
          levelNumbers.push(i);
        }
      }
    }
    
    const gameCards = [];
    
    // Create pairs of cards
    levelNumbers.forEach((number) => {
      const numberData = numbersData.find(n => n.englishNumeral === number);
      
      if (numberData) {
        // Arabic card
        gameCards.push({
          id: `arabic-${number}`,
          type: 'arabic',
          number: number,
          displayValue: numberData.arabicNumeral,
          word: numberData.arabicWord,
          pairId: number
        });
        
        // Western card
        gameCards.push({
          id: `western-${number}`,
          type: 'western',
          number: number,
          displayValue: number.toString(),
          pairId: number
        });
      }
    });
    
    // Shuffle cards
    const shuffledCards = shuffleArray(gameCards);
    setCards(shuffledCards);
    setFlippedCards([]);
    setMatchedPairs([]);
    setSelectedCard(null);
    setCorrectMatches(0);
    setAttempts(0);
    setTotalScore(0);
    setGameStartTime(Date.now());
    setLastMatchTime(Date.now());
    setIsProcessing(false);
  }, [levelConfig, numbersData, shuffleArray]);

  const handleCardClick = useCallback((card) => {
    if (isProcessing) return;
    if (flippedCards.includes(card.id)) return;
    if (matchedPairs.includes(card.id)) return;
    
    if (!selectedCard) {
      // First card selection
      setSelectedCard(card);
      setFlippedCards([card.id]);
      setLastMatchTime(Date.now());
      playWhooshSound(); // Card flip sound
    } else {
      // Second card selection
      if (selectedCard.id === card.id) return; // Same card clicked
      
      const newFlippedCards = [selectedCard.id, card.id];
      setFlippedCards(newFlippedCards);
      setIsProcessing(true);
      setAttempts(prev => prev + 1);
      
      // Check for match
      const isMatch = selectedCard.pairId === card.pairId && selectedCard.type !== card.type;
      
      if (isMatch) {
        // Match found!
        const responseTime = Date.now() - lastMatchTime;
        const speedBonus = calculateSpeedBonus(responseTime);
        const points = SCORING_CONSTANTS.BASE_POINTS + speedBonus;
        
        setTimeout(() => {
          setMatchedPairs(prev => [...prev, selectedCard.id, card.id]);
          setFlippedCards([]);
          setSelectedCard(null);
          setCorrectMatches(prev => prev + 1);
          setTotalScore(prev => prev + points);
          setIsProcessing(false);
          
          // Play audio for the matched number
          playNumberAudio(card.number);
          
          // Trigger celebration
          triggerCelebration(true);
          
          // Check if level complete
          const newMatchedCount = correctMatches + 1;
          if (newMatchedCount === levelConfig.pairs) {
            setTimeout(() => {
              handleLevelComplete();
            }, 1000);
          }
        }, 800);
      } else {
        // No match
        setSessionMistakes(prev => [...prev, {
          timestamp: Date.now(),
          type: 'wrong_match',
          attempted: `${selectedCard.displayValue} - ${card.displayValue}`,
          correctPair: selectedCard.pairId
        }]);
        playIncorrectSound();
        setTimeout(() => {
          setFlippedCards([]);
          setSelectedCard(null);
          setIsProcessing(false);
        }, 1500);
      }
    }
  }, [isProcessing, flippedCards, matchedPairs, selectedCard, lastMatchTime, calculateSpeedBonus, playNumberAudio, triggerCelebration, correctMatches, levelConfig.pairs, playWhooshSound, playIncorrectSound]);

  const handleLevelComplete = useCallback(() => {
    const accuracy = Math.round((correctMatches / attempts) * 100) || 100;
    const maxPossibleScore = levelConfig.pairs * (SCORING_CONSTANTS.BASE_POINTS + SCORING_CONSTANTS.SPEED_BONUS_MAX);
    const finalScore = totalScore + SCORING_CONSTANTS.LEVEL_COMPLETE_BONUS;
    
    console.log('🐛 DEBUG handleLevelComplete:');
    console.log('correctMatches:', correctMatches);
    console.log('attempts:', attempts);
    console.log('calculated accuracy:', accuracy);

    const earnedStars = calculateStars(accuracy, attempts, correctMatches);

    console.log('earnedStars from calculateStars:', earnedStars);
    
    const scoreData = {
      totalScore: finalScore,
      accuracy,
      stars: earnedStars,
      attempts,
      correctMatches,
      completedAt: new Date().toISOString()
    };
    
    console.log('scoreData being saved:', scoreData);
    setStars(earnedStars);
    
    // Save level data using your existing hook
    saveLevelData(level, scoreData);

    const sessionData = {
      gameType: 'arabicNumberMatching',
      level,
      sessionStartTime,
      sessionDuration: Math.round((Date.now() - sessionStartTime) / 1000), // in seconds
      totalScore: finalScore,
      accuracy,
      attempts,
      correctAnswers: correctMatches,
      mistakes: sessionMistakes,
      starsEarned: earnedStars,
      levelTitle: levelConfig.title
    };
    saveSessionData(sessionData);
    
    const nextLevel = level + 1;
    const isLastLevel = !LEVEL_CONFIGS[nextLevel];
    const passedLevel = accuracy >= levelConfig.passingScore;
    
    setLevelCompleteData({
      ...scoreData,
      currentLevel: level,
      nextLevel,
      isLastLevel,
      passedLevel,
      levelTitle: levelConfig.title,
      passingScore: levelConfig.passingScore
    });
    
    if (earnedStars > 0) {
      triggerCelebration(false, earnedStars);
    }
    
    playPerfectSound();
    setGamePhase('complete');
    setShowLevelComplete(true);
    
    if (passedLevel && !isLastLevel) {
      unlockNextLevel();
    }
  }, [correctMatches, attempts, levelConfig.pairs, levelConfig.passingScore, levelConfig.title, totalScore, calculateStars, level, triggerCelebration, playPerfectSound, saveLevelData, unlockNextLevel]);

  const startLevel = useCallback(() => {
    setSessionStartTime(Date.now());
    setGamePhase('playing');
    initializeLevel();
  }, [initializeLevel]);

  const goToMenu = useCallback(() => {
    setGamePhase('menu');
  }, []);

  const restartLevel = useCallback(() => {
    initializeLevel();
  }, [initializeLevel]);

  const handleNextLevel = useCallback(() => {
    if (levelCompleteData?.passedLevel && !levelCompleteData?.isLastLevel) {
      updateLevel(levelCompleteData.nextLevel);
    }
    setShowLevelComplete(false);
    setLevelCompleteData(null);
    setGamePhase('menu');
  }, [levelCompleteData, updateLevel]);

  const handleReplayLevel = useCallback((levelToReplay) => {
    updateLevel(levelToReplay);
    setGamePhase('menu');
  }, [updateLevel]);

  const handleResetProgress = useCallback(() => {
    setShowResetConfirmation(true);
  }, []);

  const confirmResetProgress = useCallback(() => {
    resetAllProgress();
    updateLevel(1);
    setGamePhase('menu');
    setShowResetConfirmation(false);
  }, [resetAllProgress, updateLevel]);

  const selectLevel = useCallback((newLevel) => {
    setLevel(newLevel);  // Direct level selection for card matching game
  }, [setLevel]);

  // Initialize level when numbersData loads or level changes
  useEffect(() => {
    if (numbersData && numbersData.length > 0) {
      initializeLevel();
    }
  }, [level, numbersData, initializeLevel]);

  // Start background music
  useEffect(() => {
    const musicTimer = setTimeout(() => {
      startBackgroundMusic();
    }, 1000);
    return () => clearTimeout(musicTimer);
  }, [startBackgroundMusic]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.removeEventListener('ended', audioRef.current._endHandler);
          audioRef.current.removeEventListener('error', audioRef.current._errorHandler);
          audioRef.current.src = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+Dyvmwa';
          audioRef.current.load();
        } catch (e) {
          // Ignore
        }
        audioRef.current = null;
      }
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
        audioTimeoutRef.current = null;
      }
      
      // Clear confetti
      confetti.reset();
    };
  }, []);

  // Components
  const StarDisplay = React.memo(({ count, size = "text-2xl" }) => {
    console.log('🐛 StarDisplay rendering with count:', count);
    
    return (
        <div className={`flex justify-center gap-1 ${size}`}>
        {[1, 2, 3].map(star => {
            const isActive = star <= count;
            console.log(`🐛 Star ${star}: isActive = ${isActive}, count = ${count}`);
            
            return (
            <span 
                key={star} 
                className={`drop-shadow-lg transition-all duration-300 ${isActive ? 'animate-pulse' : ''}`}
                style={{ 
                color: isActive ? '#fbbf24' : '#d1d5db' 
                }}
            >
                {isActive ? '⭐' : '☆'}
            </span>
            );
        })}
        </div>
    );
  });

  StarDisplay.propTypes = {
    count: PropTypes.number.isRequired,
    size: PropTypes.string
  };

  const Card = React.memo(({ card }) => {
    const isFlipped = flippedCards.includes(card.id) || matchedPairs.includes(card.id);
    const isMatched = matchedPairs.includes(card.id);
    const isSelected = selectedCard?.id === card.id;
    
    // Determine if this is a large number that needs special handling
    const isLargeNumber = card.type === 'arabic' && card.number >= 10;
    const isVeryLargeNumber = card.type === 'arabic' && card.number >= 100;
    
    return (
      <div
        onClick={() => handleCardClick(card)}
        className={`
          relative w-full aspect-square rounded-2xl border-4 cursor-pointer transform transition-all duration-500 hover:scale-105
          ${isFlipped 
            ? isMatched 
              ? 'bg-gradient-to-br from-green-400 to-green-500 border-green-600 shadow-2xl' 
              : isSelected
                ? 'bg-gradient-to-br from-blue-400 to-blue-500 border-blue-600 shadow-xl'
                : 'bg-gradient-to-br from-purple-400 to-purple-500 border-purple-600 shadow-xl'
            : 'bg-gradient-to-br from-gray-300 to-gray-400 border-gray-500 shadow-lg hover:shadow-xl'
          }
        `}
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(0deg)' : 'rotateY(180deg)'
        }}
      >
        {/* Card Back */}
        <div 
          className="absolute inset-0 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 border-4 border-white flex items-center justify-center"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }}
        >
          <div className="text-4xl md:text-6xl">❓</div>
        </div>
        
        {/* Card Front */}
        <div 
          className="absolute inset-0 rounded-2xl flex flex-col items-center justify-center p-2"
          style={{
            backfaceVisibility: 'hidden'
          }}
        >
          {card.type === 'arabic' ? (
            // Arabic card with adaptive layout
            <div className="flex flex-col items-center justify-center h-full w-full">
              {/* Arabic numeral - adaptive sizing */}
              <div className={`font-black text-white drop-shadow-2xl text-center leading-tight ${
                isVeryLargeNumber ? 'text-2xl md:text-4xl' : 
                isLargeNumber ? 'text-3xl md:text-5xl' : 
                'text-4xl md:text-6xl'
              }`}>
                {card.displayValue}
              </div>
              
              {/* Arabic word - conditional display and adaptive sizing */}
              {card.word && (
                <div className={`text-white font-bold text-center mt-1 leading-tight ${
                  isVeryLargeNumber ? 'text-xs hidden md:block' : // Hide on mobile for very large numbers
                  isLargeNumber ? 'text-xs md:text-sm' : 
                  'text-xs md:text-sm'
                }`}>
                  {/* Truncate very long words on mobile */}
                  <span className="md:hidden">
                    {card.word.length > 12 ? `${card.word.substring(0, 10)}...` : card.word}
                  </span>
                  <span className="hidden md:inline">
                    {card.word}
                  </span>
                </div>
              )}
            </div>
          ) : (
            // Western number card - simpler layout
            <div className={`font-black text-white drop-shadow-2xl ${
              card.number >= 100 ? 'text-3xl md:text-5xl' : 'text-4xl md:text-6xl'
            }`}>
              {card.displayValue}
            </div>
          )}
          
          {/* Audio button for matched cards */}
          {isMatched && (
            <div className="absolute -top-2 -right-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  playNumberAudio(card.number);
                }}
                disabled={audioPlaying}
                className="bg-yellow-400 text-yellow-800 p-2 rounded-full shadow-lg hover:bg-yellow-300 transition-all duration-200"
              >
                <Volume2 size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  });

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full">
          <div className="text-center">
            <div className="text-4xl md:text-6xl mb-4 animate-spin">🎴</div>
            <div className="text-purple-600 text-xl md:text-3xl font-bold animate-pulse">Loading Card Match...</div>
            <div className="text-gray-600 text-sm md:text-lg mt-2">Getting ready for fun!</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="text-4xl md:text-6xl mb-4">😢</div>
          <h2 className="text-xl md:text-3xl font-bold text-red-600 mb-4">Oops! Something went wrong</h2>
          <p className="text-gray-700 mb-6 text-sm md:text-lg">{error?.message || 'Something went wrong'}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-500 to-indigo-600 p-4">
      <AudioElements />
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4 md:mb-8">
          <h1 className="text-3xl md:text-6xl font-black text-white mb-2 md:mb-4 drop-shadow-2xl">
            🎴 Flip & Find: Number Quest 🎴
          </h1>
          <p className="text-lg md:text-2xl text-white font-bold opacity-90 drop-shadow-lg">
            Match Arabic numerals with Western numbers!
          </p>
        </div>

        {gamePhase === 'menu' && (
          <div className="max-w-4xl mx-auto">
            {/* Level Info */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-4 md:mb-8 border-4 border-yellow-300">
              <h2 className="text-2xl md:text-4xl font-bold text-center mb-2 md:mb-4 text-purple-600">
                🎮 Level {level}: {levelConfig.title}
              </h2>
              <p className="text-center text-gray-700 mb-4 md:mb-6 text-lg md:text-xl font-semibold">
                {levelConfig.description}
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6 mb-4 md:mb-8">
                <div className="text-center bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🎯</div>
                  <p className="font-bold text-sm md:text-lg">Pairs</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.pairs}</p>
                </div>
                <div className="text-center bg-gradient-to-br from-green-400 to-green-600 rounded-2xl p-3 md:p-6 text-white">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🔢</div>
                  <p className="font-bold text-sm md:text-lg">Numbers</p>
                  <p className="text-lg md:text-2xl font-black">
                    {levelConfig.isRandom ? `Random ${levelConfig.range[0]}-${levelConfig.range[1]}` : `${levelConfig.range[0]}-${levelConfig.range[1]}`}
                  </p>
                </div>
                <div className="text-center bg-gradient-to-br from-purple-400 to-purple-600 rounded-2xl p-3 md:p-6 text-white col-span-2 md:col-span-1">
                  <div className="text-3xl md:text-5xl mb-1 md:mb-3">🏆</div>
                  <p className="font-bold text-sm md:text-lg">Pass</p>
                  <p className="text-lg md:text-2xl font-black">{levelConfig.passingScore}%</p>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={startLevel}
                  className="px-8 md:px-12 py-4 md:py-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-3xl font-black text-xl md:text-3xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300 border-4 border-white"
                >
                  🎴 START MATCHING! 🎴
                </button>
              </div>
            </div>

            {/* Level Grid */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 border-4 border-pink-300">
            <h3 className="text-xl md:text-3xl font-bold text-center mb-4 md:mb-6 text-purple-600">
                🎮 Choose Your Level!
            </h3>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-2 md:gap-4">
                {Object.entries(LEVEL_CONFIGS).map(([levelNum, config]) => {
                const num = parseInt(levelNum);
                const isCurrent = num === level;
                const levelScore = bestScores[`level${num}`];
                const hasBeenCompleted = levelScore && levelScore.completedAt && levelScore.stars !== undefined;
                
                return (
                    <button
                    key={num}
                    onClick={() => selectLevel(num)}  // This should work for ALL levels
                    className={`
                        relative p-2 md:p-4 rounded-2xl text-sm md:text-lg font-bold transition-all duration-300 min-h-[80px] md:min-h-[120px] flex flex-col items-center justify-center border-2 md:border-4 cursor-pointer
                        ${isCurrent 
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-2xl transform scale-105 md:scale-110 border-yellow-300' 
                        : 'bg-gradient-to-br from-green-100 to-green-200 text-green-800 hover:from-green-200 hover:to-green-300 hover:scale-105 border-green-300'
                        }
                    `}
                    title={config.title}
                    >
                    <div className="text-lg md:text-2xl font-black">{num}</div>
                    <div className="text-xs mt-1 font-bold hidden md:block">{config.pairs} pairs</div>
                    <div className="text-xs font-bold md:hidden">{config.pairs}p</div>
                    {config.isRandom && (
                        <div className="text-xs text-center font-bold opacity-75">
                        Random
                        </div>
                    )}
                    {hasBeenCompleted && (
                        <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2">
                        <StarDisplay count={levelScore.stars || 0} size="text-xs md:text-sm" />
                        </div>
                    )}
                    </button>
                );
                })}
            </div>
              
              <div className="mt-4 md:mt-8 flex gap-2 md:gap-4 justify-center flex-wrap">
                <Link to="/">
                  <button 
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    🔙 Main Menu
                  </button>
                </Link>                
                <button 
                  onClick={handleResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🗑️ Reset Progress
                </button>
                <button 
                  onClick={() => setShowInstructions(true)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  📖 How to Play
                </button>
              </div>
            </div>
          </div>
        )}

        {gamePhase === 'playing' && (
          <div className="max-w-4xl mx-auto">
            {/* Game Stats */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 mb-6 shadow-xl">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🎯</div>
                  <p className="font-bold text-lg md:text-xl">{totalScore}</p>
                  <p className="text-xs font-bold">Score</p>
                </div>
                <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">✅</div>
                  <p className="font-bold text-lg md:text-xl">{correctMatches}/{levelConfig.pairs}</p>
                  <p className="text-xs font-bold">Matches</p>
                </div>
                <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">🔄</div>
                  <p className="font-bold text-lg md:text-xl">{attempts}</p>
                  <p className="text-xs font-bold">Attempts</p>
                </div>
                <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl p-3 text-white">
                  <div className="text-2xl md:text-3xl mb-1">📊</div>
                  <p className="font-bold text-lg md:text-xl">{attempts > 0 ? Math.round((correctMatches / attempts) * 100) : 100}%</p>
                  <p className="text-xs font-bold">Accuracy</p>
                </div>
              </div>
            </div>

            {/* Game Board */}
            <div className="bg-white rounded-3xl shadow-2xl p-4 md:p-8 mb-6 border-4 border-yellow-300">
              <div className={`grid gap-3 md:gap-4 ${
                levelConfig.pairs <= 4 ? 'grid-cols-4' : 
                levelConfig.pairs <= 6 ? 'grid-cols-4 md:grid-cols-6' : 
                'grid-cols-4 md:grid-cols-6 lg:grid-cols-8'
              }`}>
                {cards.map((card) => (
                  <Card key={card.id} card={card} />
                ))}
              </div>
            </div>

            {/* Controls */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 md:p-6 shadow-xl">
              <div className="flex gap-3 md:gap-4 justify-center flex-wrap">
                <button 
                  onClick={goToMenu}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <Home size={20} />
                  Menu
                </button>
                <button 
                  onClick={restartLevel}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <RotateCcw size={20} />
                  Restart
                </button>
                <button 
                  onClick={() => initializeLevel()}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <Shuffle size={20} />
                  Shuffle
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Celebration Effects */}
        {celebrations && (
          <div className="fixed inset-0 pointer-events-none z-40">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-6xl md:text-9xl animate-bounce">🎉</div>
            </div>
          </div>
        )}

        {sparkles && (
          <div className="fixed inset-0 pointer-events-none z-40">
            {[...Array(15)].map((_, i) => (
              <div
                key={`sparkle-${i}`}
                className="absolute animate-ping text-yellow-400 text-2xl"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              >
                ✨
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-purple-300">
            <div className="p-4 md:p-8">
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <h2 className="text-2xl md:text-3xl font-bold text-purple-600">📖 How to Play!</h2>
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl md:text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 md:space-y-6">
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-blue-300">
                  <h3 className="font-bold text-blue-800 mb-2 md:mb-3 text-lg md:text-xl">🎯 Your Mission</h3>
                  <p className="text-blue-700 text-sm md:text-lg">Match Arabic numerals with their Western number equivalents. Find all pairs to complete the level!</p>
                </div>
                
                <div className="bg-gradient-to-r from-green-100 to-green-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-green-300">
                  <h3 className="font-bold text-green-800 mb-2 md:mb-3 text-lg md:text-xl">🎮 How to Play</h3>
                  <ul className="list-disc list-inside text-green-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Click on a card to flip it and see the number</li>
                    <li>Click on another card to try to make a match</li>
                    <li>Match Arabic numerals (١, ٢, ٣) with Western numbers (1, 2, 3)</li>
                    <li>When you make a correct match, you'll hear the Arabic pronunciation!</li>
                    <li>Complete all pairs to finish the level</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-yellow-100 to-yellow-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-yellow-300">
                  <h3 className="font-bold text-yellow-800 mb-2 md:mb-3 text-lg md:text-xl">🏆 Scoring</h3>
                  <ul className="list-disc list-inside text-yellow-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Earn points for each correct match</li>
                    <li>Get speed bonuses for quick matches</li>
                    <li>Maintain high accuracy for star ratings</li>
                    <li>Click the volume button on matched cards to replay audio</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-purple-100 to-purple-200 p-4 md:p-6 rounded-2xl border-2 md:border-4 border-purple-300">
                  <h3 className="font-bold text-purple-800 mb-2 md:mb-3 text-lg md:text-xl">💡 Tips</h3>
                  <ul className="list-disc list-inside text-purple-700 space-y-1 md:space-y-2 text-sm md:text-lg">
                    <li>Remember the positions of cards you've seen</li>
                    <li>Try to match quickly for bonus points</li>
                    <li>Listen to the Arabic pronunciation to learn</li>
                    <li>Each level introduces new numbers to master</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 md:mt-8 text-center">
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Let's Start Matching! 🎴
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Level Complete Modal */}
      {showLevelComplete && levelCompleteData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-4 md:p-8 border-4 border-yellow-300">
            <div className="text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">
                {levelCompleteData.passedLevel ? '🎉' : '😊'}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-purple-600 mb-2 md:mb-3">
                Level {levelCompleteData.currentLevel} Complete!
              </h2>
              <h3 className="text-lg md:text-xl text-gray-700 mb-4 md:mb-6 font-semibold">
                {levelCompleteData.levelTitle}
              </h3>
              
              <div className="mb-6 md:mb-8">
                <StarDisplay count={levelCompleteData.stars} size="text-3xl md:text-4xl" />
                <p className="text-xl md:text-2xl font-bold text-indigo-600 mt-3 md:mt-4">
                  Final Score: {levelCompleteData.totalScore}
                </p>
                <p className="text-base md:text-lg text-gray-600 mt-2">
                  Accuracy: {levelCompleteData.accuracy}% • Attempts: {levelCompleteData.attempts}
                </p>
              </div>

              <div className="mb-6 md:mb-8">
                {levelCompleteData.passedLevel ? (
                <div className="bg-gradient-to-r from-green-100 to-green-200 border-2 md:border-4 border-green-400 rounded-2xl p-4 md:p-6">
                    <p className="text-green-800 font-bold text-lg md:text-xl">
                    ✅ Fantastic! You passed! 🎉
                    </p>
                    <p className="text-green-700 text-base md:text-lg">
                    {levelCompleteData.accuracy}% ≥ {levelCompleteData.passingScore}%
                    </p>
                    {!levelCompleteData.isLastLevel && (
                    <p className="text-green-700 text-base md:text-lg mt-2">Great job! You can now try the next level!</p>
                    )}
                </div>
                ) : (
                <div className="bg-gradient-to-r from-orange-100 to-orange-200 border-2 md:border-4 border-orange-400 rounded-2xl p-4 md:p-6">
                    <p className="text-orange-800 font-bold text-lg md:text-xl">
                    😊 Good try! Practice makes perfect!
                    </p>
                    <p className="text-orange-700 text-base md:text-lg">
                    You need {levelCompleteData.passingScore}% accuracy to pass
                    </p>
                    <p className="text-orange-700 text-base md:text-lg">Keep practicing to earn those stars! 🌟</p>
                </div>
                )}
              </div>

              <div className="space-y-3 md:space-y-4">
                <button 
                  onClick={() => { 
                    setShowLevelComplete(false); 
                    setGamePhase('menu'); 
                  }}
                  className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  🔄 Play Again
                </button>
                
                {levelCompleteData.passedLevel && !levelCompleteData.isLastLevel && (
                  <button 
                    onClick={handleNextLevel}
                    className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                  >
                    🚀 Next Level! 🚀
                  </button>
                )}
                
                {levelCompleteData.isLastLevel && (
                  <button 
                    onClick={() => { 
                      setShowLevelComplete(false); 
                      setGamePhase('menu'); 
                    }}
                    className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300"
                  >
                    🎊 You're a Card Master! 🎊
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-8 border-4 border-red-300">
            <div className="text-center">
              <div className="text-6xl md:text-8xl mb-4 md:mb-6">⚠️</div>
              <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-3 md:mb-4">Reset Everything?</h2>
              <p className="text-gray-700 mb-6 md:mb-8 text-sm md:text-lg">
                This will delete all your amazing scores, stars, and progress. You'll start from Level 1 again.
                This cannot be undone!
              </p>
              <div className="flex gap-3 md:gap-4 justify-center">
                <button 
                  onClick={() => setShowResetConfirmation(false)}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmResetProgress}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Yes, Reset Everything
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArabicNumberCardMatchingGame;